/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {ForwardingModeDropdownProps, getSipStatusLabelValue, styles, useStyles} from './ForwardingModeDropdown.utils';
import Select, { components  } from 'react-select';
import { DefaultAnsweringActionFlag, ServiceFeatureName } from '../../../store/types/ServiceFeature';
import classNames from 'classnames';
import { YesNo } from '../../../store/types/CallScreening';

const ForwardingModeDropdown: React.VFC<ForwardingModeDropdownProps> = (
{
    account,
    onChange
}) => {

    const {t} = useTranslation();
    const classes = useStyles();

    const statusLabel = useMemo(() => {
        return getSipStatusLabelValue(account, t);
    }, [account]);
    
    const nameLabel = useMemo(() => {
        if(account.extension_name?.length) {
            return account.extension_id + ' – ' + account.extension_name;
        }
        return account.extension_id;
    }, [account]);

    const CustomOption = (props: any) => {
        const { isSelected, children } = props;
        return (
            <components.Option {...props}>
            <div className={classes.customOptionContainer} style={{
                fontWeight: isSelected ? 'bold' : 'normal',
            }}>
                <div>{children}</div>
                {isSelected && (
                    <div style={{ marginLeft: 'auto', color: 'black' }}>✔</div>
                )}
            </div>
            </components.Option>
        );
    };

    const SingleValue = (props: any) => {
        const { data } = props;

        return (
            <components.SingleValue {...props}>
            <div className={classes.choosenValueContainer}>
                <div className={classes.choosenValueTopContainer}>
                    <div className={classNames(classes.accountStatusLabel,
                        account.blocked == YesNo.No && account.sip_status == 1
                            ? classes.accountStatusLabelActive
                            : classes.accountStatusLabelInactive
                    )}>
                        {statusLabel}
                    </div>

                    {nameLabel?.length && (
                        <div className={classes.accountNameLabel}>
                            {t('screens:accountDashboard.ext')} 
                            {nameLabel}
                        </div>
                    )}
                </div>

                <div className={classes.choosenValue}>
                    {data.label}
                </div>
            </div>
            </components.SingleValue>
        );
    };
    
    const DropdownIndicator = (props: any) => {
        const { selectProps: { menuIsOpen } } = props;

        return (
            <components.DropdownIndicator {...props}>
            <div className={classes.dropdownIndicator} style={{
                transform: menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
                <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M0 0L5 5L10 0H0Z" fill="#888888"/>
                </svg>
            </div>
            </components.DropdownIndicator>
        );
    };

    const CustomGroupHeading = (props: any) => {
        return (
            <components.GroupHeading {...props}>
            <div className={classes.customGroupHeader}>
                {props.children}
            </div>
            </components.GroupHeading>
        );
    };

    const defaultAnsweringModes = useMemo(() => {
        const options = Object.values(DefaultAnsweringActionFlag).map((v) => ({
            label: t(`enums:defaultAnsweringActionEnum.${v}`),
            value: v,
        }));
        return [{
            label: t('screens:extensions.defaultAnsweringMode'),
            options
        }];
    }, []);

    const selectedValue = useMemo(() => {
        return (account?.service_features
            ?.find(e => e.name == ServiceFeatureName.DefaultAction)
            ?.effective_flag_value as DefaultAnsweringActionFlag) ?? DefaultAnsweringActionFlag.RingForwardVoicemail;
    }, [account]);

    return (<Select
        options={defaultAnsweringModes}
        components={{
            Option: CustomOption,
            SingleValue: SingleValue,
            DropdownIndicator: DropdownIndicator,
            IndicatorSeparator: () => null,
            GroupHeading: CustomGroupHeading
        }}
        isSearchable={false}
        tabSelectsValue={false}
        openMenuOnFocus={false}
        defaultValue={defaultAnsweringModes[0].options.find(e => e.value == selectedValue)}
        onChange={(v) => {
            onChange?.(v?.value ?? selectedValue)
        }}
        styles={styles}
    />);
};

export default ForwardingModeDropdown;
